const BASE_URL = "https://snooze-bedbuilder.s3.ap-southeast-2.amazonaws.com/components";

export default {
  namespaced: true,

  state: () => ({
    availableOptions: [],
    baseOptions: [],
    optionsColours: [],
    headboardDrawers: null,
    activeOption: null,
    isSubOptionsModalOpen: false,
    showBedBuilderReset: false,
    showBedBuilderOptionsChange: false,
    activeOptions: null,
    activeImages: null,
    activeImagesBase64: null,
    headerBase64: null,
    isGeneratingPDF: false,
  }),

  getters: {
    showOptionsModalOpen(state) {
      return state.isSubOptionsModalOpen;
    },

    selectedOptionValues(state) {
      const { activeOption, activeOptions, availableOptions, optionsColours } =
        state;

      const createImageOption = (name, prefix = "", colour) => {
        const formattedName = name
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/\//g, "-")
          .replace(/\+/g, "plus");

        return {
          name,
          image: {
            url: `${BASE_URL}/options/${prefix}${formattedName}.png`,
            alt: `${name} image`,
          },
          colour,
        };
      };

      if (activeOption === "Material") {
        return Object.keys(availableOptions).map(name =>
          createImageOption(name)
        );
      }

      if (activeOption === "Colour") {
        const materialOptions = availableOptions[activeOptions.Material];
        return Object.keys(materialOptions || {}).map(name =>
          createImageOption(
            name,
            `${activeOptions.Material.toLowerCase()}_`,
            optionsColours.get(name)
          )
        );
      }

      if (activeOption === "Base" || activeOption === "Headboard") {
        const entry =
          availableOptions[activeOptions.Material]?.[activeOptions.Colour]?.[
            activeOption
          ];
        return Array.from(entry || []).map(name =>
          createImageOption(name, `${activeOptions.Material.toLowerCase()}_`)
        );
      }

      return [];
    },
  },

  actions: {
    init(
      { commit },
      { availableOptions, baseOptions, optionsColours, headboardDrawers }
    ) {
      commit("INIT", {
        availableOptions,
        baseOptions,
        optionsColours,
        headboardDrawers,
      });
    },
    setActiveOption({ commit }, option) {
      commit("SET_ACTIVE_OPTION", null);
      commit("SET_SUB_OPTIONS_MODAL_OPEN", true);
      commit("SET_ACTIVE_OPTION", option);
    },
    closeSubOptionsModal({ commit }) {
      commit("SET_ACTIVE_OPTION", null);
      commit("SET_SUB_OPTIONS_MODAL_OPEN", false);
    },
    openBedbuilderReset({ commit }) {
      commit("SET_SHOW_BEDBUILDER_RESET", true);
    },
    closeBedbuilderReset({ commit }) {
      commit("SET_SHOW_BEDBUILDER_RESET", false);
      commit("SET_SUB_OPTIONS_MODAL_OPEN", false);
      commit("SET_ACTIVE_OPTION", null);
    },
    closeBedbuilderOptionsChange({ commit }) {
      commit("SET_SHOW_BEDBUILDER_OPTIONS_CHANGE", false);
      commit("SET_SUB_OPTIONS_MODAL_OPEN", false);
      commit("SET_ACTIVE_OPTION", null);
    },
    openBedbuilderOptionsChange({ commit }) {
      commit("SET_SHOW_BEDBUILDER_OPTIONS_CHANGE", true);
    },
    setPDFLoading({ commit }, payload) {
      commit("SET_PDF_LOADING", payload);
    },

    setCoreOptions({ commit }, coreOptions) {
      commit("SET_CORE_OPTIONS", coreOptions);
    },
    async setActiveOptions({ commit, state }, activeOptions) {
      commit("SET_ACTIVE_IMAGES_BASE64", null);

      const normaliseName = (material, color, type) => {
        return `${material}_${color}_${type}`
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/\//g, "-")
          .replace(/\+/g, "plus");
      };

      const { Material, Colour, Base, Headboard } = activeOptions;

      const activeImages = {
        base: `${BASE_URL}/base/${normaliseName(Material, Colour, Base)}.png`,
        headboard: `${BASE_URL}/headboard/${normaliseName(
          Material,
          Colour,
          Headboard
        )}.png`,
        mattress: `${BASE_URL}/others/mattress.png`,
        logo: `${BASE_URL}/others/logo.png`,
        header: `${BASE_URL}/others/pdf_header.png`,
        timberMattress: `${BASE_URL}/others/timber_mattress.png`,
        upholsteredMattress: `${BASE_URL}/others/upholstered_mattress.png`,
      };

      if (state.headboardDrawers?.[Headboard] === true) {
        activeImages.drawers = `${BASE_URL}/headboard/drawers/${normaliseName(
          Material,
          Colour,
          Headboard
        )}.png`;
      }

      commit("SET_ACTIVE_IMAGES", activeImages);
      commit("SET_ACTIVE_OPTIONS", activeOptions);

      const {
        base,
        headboard,
        mattress,
        header,
        timberMattress,
        upholsteredMattress,
        drawers,
      } = activeImages;

      const requests = [
        this.$axios.get(
          `/api/base64-conversion?url=${encodeURIComponent(base)}`
        ),
        this.$axios.get(
          `/api/base64-conversion?url=${encodeURIComponent(headboard)}`
        ),
        this.$axios.get(
          `/api/base64-conversion?url=${encodeURIComponent(mattress)}`
        ),
        this.$axios.get(
          `/api/base64-conversion?url=${encodeURIComponent(timberMattress)}`
        ),
        this.$axios.get(
          `/api/base64-conversion?url=${encodeURIComponent(
            upholsteredMattress
          )}`
        ),
      ];

      let headerPromise = null;
      if (!state.headerBase64) {
        headerPromise = this.$axios.get(
          `/api/base64-conversion?url=${encodeURIComponent(header)}`
        );
      }

      let drawersPromise = null;
      if (state.headboardDrawers?.[Headboard] === true && drawers) {
        drawersPromise = this.$axios.get(
          `/api/base64-conversion?url=${encodeURIComponent(drawers)}`
        );
      }

      const [
        baseRes,
        headboardRes,
        mattressRes,
        timberMattressRes,
        upholsteredMattressRes,
        drawersRes,
        headerRes,
      ] = await Promise.all([...requests, drawersPromise, headerPromise]);

      const activeImagesBase64 = {
        base: baseRes.data,
        headboard: headboardRes.data,
        mattress: mattressRes.data,
        timberMattress: timberMattressRes.data,
        upholsteredMattress: upholsteredMattressRes.data,
        drawers: drawersRes ? drawersRes.data : null,
      };

      commit("SET_ACTIVE_IMAGES_BASE64", activeImagesBase64);

      if (headerRes) {
        commit("SET_HEADER_BASE64", headerRes.data);
      }
    },
  },

  mutations: {
    INIT(
      state,
      { availableOptions, baseOptions, optionsColours, headboardDrawers }
    ) {
      state.availableOptions = availableOptions;
      state.baseOptions = baseOptions;
      state.optionsColours = optionsColours;
      state.headboardDrawers = headboardDrawers;
    },
    SET_SUB_OPTIONS_MODAL_OPEN(state, flag) {
      state.isSubOptionsModalOpen = flag;
    },
    SET_ACTIVE_OPTION(state, option) {
      state.activeOption = option;
    },
    SET_SHOW_BEDBUILDER_RESET(state, flag) {
      state.showBedBuilderReset = flag;
    },
    SET_SHOW_BEDBUILDER_OPTIONS_CHANGE(state, flag) {
      state.showBedBuilderOptionsChange = flag;
    },
    SET_CORE_OPTIONS(state, coreOptions) {
      state.coreOptions = coreOptions;
    },
    SET_ACTIVE_OPTIONS(state, activeOptions) {
      state.activeOptions = activeOptions;
    },
    SET_ACTIVE_IMAGES(state, activeImages) {
      state.activeImages = activeImages;
    },
    SET_ACTIVE_IMAGES_BASE64(state, activeImagesBase64) {
      state.activeImagesBase64 = activeImagesBase64;
    },
    SET_PDF_LOADING(state, status) {
      state.isGeneratingPDF = status;
    },
    SET_HEADER_BASE64(state, headerBase64) {
      state.headerBase64 = headerBase64;
    },
  },
};
